import React from 'react';
import { useAuthStore } from '../store';
import { translations } from '../translations';

// Datos simulados del usuario de Caldea
const mockUserData = {
  name: 'María García',
  membership: 'Club Caldea Premium',
  avatar: '👩',
  improvementLevel: 125, // Changed from points
  streak: 8,
  level: 'Gold',
  weeklyVisits: 3,
  monthlyGoal: 12,
  monthlyProgress: 9
};

// Últimas sesiones de entrenamiento (datos de Technogym/Mywellness simulados)
const recentSessions = [
  { id: 1, type: 'Cardio', duration: '45 min', calories: 420, distance: '5.2 km', date: '2026-01-27', triggerKey: 'sessionUnlocked' },
  { id: 2, type: 'Fuerza', duration: '55 min', calories: 380, sets: 24, date: '2026-01-26', triggerKey: 'upgradeAvailable' },
  { id: 3, type: 'Cardio', duration: '30 min', calories: 280, distance: '3.1 km', date: '2026-01-25', triggerKey: null },
  { id: 4, type: 'Wellness', duration: '20 min', calories: 80, activity: 'Breathwork', date: '2026-01-24', triggerKey: 'sessionCompleted' },
];

export default function DashboardPage() {
  const { language } = useAuthStore();
  const t = translations[language];

  // Rewards disponibles para canjear ahora (inside component to access 't')
  const availableRewards = [
    { id: 1, name: t.descontracturante, reqLevel: 15, icon: '💆', ready: true, reason: t.reasonCardio },
    { id: 2, name: t.hydration, reqLevel: 5, icon: '🍵', ready: true, reason: t.reasonStreak },
  ];

  const activeChallenges = [
    { id: 1, name: 'Semana Wellness', progress: 75, reward: 'Upgrade Recovery', daysLeft: 2 },
    { id: 2, name: 'Runner Caldea', progress: 74, reward: 'Pack Premium', daysLeft: 4 },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 md:py-8 space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-700 rounded-2xl p-6 md:p-8 text-white shadow-lg">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="flex items-center gap-4 w-full">
            <div className="text-5xl md:text-6xl animate-bounce-slow">{mockUserData.avatar}</div>
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold">{t.hello.replace('{name}', mockUserData.name.split(' ')[0])}</h1>
              <p className="text-cyan-100 text-sm md:text-base">{mockUserData.membership}</p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="bg-amber-400 text-amber-900 px-3 py-1 rounded-full text-xs md:text-sm font-semibold shadow-sm">
                  {mockUserData.level}
                </span>
                <span className="text-cyan-100 text-xs md:text-sm bg-white/10 px-2 py-1 rounded-lg">🔥 {mockUserData.streak} {t.streak}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 w-full md:w-auto bg-white/10 p-4 rounded-xl backdrop-blur-sm">
            <div className="text-center">
              <div className="text-2xl md:text-4xl font-bold">{mockUserData.improvementLevel}</div>
              <div className="text-xs md:text-sm text-cyan-100">{t.instances}</div>
            </div>
            <div className="text-center border-l border-white/20">
              <div className="text-2xl md:text-4xl font-bold">{mockUserData.monthlyProgress}/{mockUserData.monthlyGoal}</div>
              <div className="text-xs md:text-sm text-cyan-100">{t.sessionsMonth}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Slider/Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 transition-colors">
          <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">{t.visits}</div>
          <div className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">{mockUserData.weeklyVisits}</div>
          <div className="text-green-500 text-xs font-medium">↑ +1 {t.vsPrev}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 transition-colors">
          <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">{t.calories}</div>
          <div className="text-2xl font-bold text-orange-500">1,160</div>
          <div className="text-gray-400 dark:text-gray-500 text-xs">{t.thisWeek}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 transition-colors">
          <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">{t.redeemed}</div>
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">4</div>
          <div className="text-gray-400 dark:text-gray-500 text-xs">{t.thisMonth}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 transition-colors">
          <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Challenges</div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">2</div>
          <div className="text-gray-400 dark:text-gray-500 text-xs">{t.active}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Rewards listos para canjear */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 transition-colors">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 dark:text-white">
              {t.rewardsReady}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {availableRewards.map((reward) => (
                <div key={reward.id} className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-2 border-green-500 dark:border-green-600 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-3xl">{reward.icon}</span>
                    <div>
                      <h3 className="font-semibold dark:text-white">{reward.name}</h3>
                      <p className="text-sm text-green-600 dark:text-green-400">{reward.reason}</p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-cyan-600 dark:text-cyan-400 font-bold">{t.level} {reward.reqLevel} {t.required}</span>
                    <a 
                      href="/rewards" 
                      className="bg-green-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-600 transition"
                    >
                      {t.redeemNow}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Últimas sesiones */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 transition-colors">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 dark:text-white">
              {t.recentSessions}
            </h2>
            <div className="space-y-4">
              {recentSessions.map((session) => (
                <div key={session.id} className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl transition-colors">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                    session.type === 'Cardio' ? 'bg-red-100 dark:bg-red-900/50' : 
                    session.type === 'Fuerza' ? 'bg-blue-100 dark:bg-blue-900/50' : 'bg-purple-100 dark:bg-purple-900/50'
                  }`}>
                    {session.type === 'Cardio' ? '🏃' : session.type === 'Fuerza' ? '💪' : '🧘'}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold dark:text-white">{t[session.type.toLowerCase()] || session.type}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-300">
                          {session.duration} • {session.calories} kcal
                          {session.distance && ` • ${session.distance}`}
                          {session.sets && ` • ${session.sets} ${t.series}`}
                        </p>
                      </div>
                      <span className="text-sm text-gray-400">{session.date}</span>
                    </div>
                    {session.triggerKey && (
                      <p className="text-sm text-green-600 dark:text-green-400 mt-2 bg-green-50 dark:bg-green-900/30 px-3 py-1 rounded-full inline-block">
                        {t[session.triggerKey]}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Challenges activos */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 transition-colors">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2 dark:text-white">
              🏆 {t.challenges}
            </h3>
            <div className="space-y-4">
              {activeChallenges.map((challenge) => (
                <div key={challenge.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-xl transition-colors">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold dark:text-white">{challenge.name}</h4>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{challenge.daysLeft} {t.days}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 mb-2">
                    <div 
                      className="bg-cyan-500 h-2 rounded-full" 
                      style={{ width: `${challenge.progress}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">🎁 {challenge.reward}</p>
                </div>
              ))}
            </div>
            <a href="/challenges" className="block text-center text-cyan-600 dark:text-cyan-400 font-semibold mt-4 hover:underline">
              Ver todos los challenges →
            </a>
          </div>

          {/* Journey Move → Recover → Feel */}
          <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl p-6 text-white">
            <h3 className="font-bold mb-3">Tu Journey Caldea</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">✓</div>
                <div>
                  <p className="font-semibold">Move</p>
                  <p className="text-sm text-cyan-100">5.2km cardio hoy</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/30 rounded-full flex items-center justify-center animate-pulse">→</div>
                <div>
                  <p className="font-semibold">Recover</p>
                  <p className="text-sm text-cyan-100">¡Canjea tu descarga!</p>
                </div>
              </div>
              <div className="flex items-center gap-3 opacity-60">
                <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">○</div>
                <div>
                  <p className="font-semibold">Feel Better</p>
                  <p className="text-sm text-cyan-100">Próximo paso</p>
                </div>
              </div>
            </div>
          </div>

          {/* Próximo nivel */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 transition-colors">
            <h3 className="text-lg font-bold mb-3 dark:text-white">Próximo Nivel: Platinum</h3>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3 mb-2">
              <div className="bg-gradient-to-r from-amber-400 to-amber-600 h-3 rounded-full" style={{ width: '62%' }} />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">75 {t.level} más para desbloquear beneficios Platinum</p>
          </div>
        </div>
      </div>
    </div>
  );
}
